import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, LogIn } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import {
  rememberFamily,
  getRememberedFamily,
  getDeviceId,
  getDeviceLabel,
  REMEMBER_DAYS,
} from "@/lib/familyDevice";

export default function FamilyLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // If this device already remembers a family, jump straight into their card.
  useEffect(() => {
    const remembered = getRememberedFamily();
    if (remembered) {
      nav(`/family/${remembered}`, { replace: true });
    }
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/families/family-login", {
        email,
        password,
      });
      if (remember) {
        rememberFamily(data.magic_token);
        // Register this browser in the family's server-side trusted-devices
        // list so it shows up under "Trusted devices" in the portal and can
        // be revoked from any other browser.
        try {
          await api.post(`/families/token/${data.magic_token}/devices`, {
            device_id: getDeviceId(),
            label: getDeviceLabel(),
          });
        } catch {
          // Non-fatal — the client-side remember still works.
        }
      }
      toast.success("Welcome back — opening your family card.");
      nav(`/family/${data.magic_token}`);
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Couldn't sign in. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="max-w-5xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group">
          <img
            src="/bailey-logo.png"
            alt=""
            className="h-9 w-9 rounded-full border border-[#e6e2d6]"
          />
          <span className="font-serif text-xl text-stone-900">
            Bailey Family Reunion
          </span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 rounded-full border border-[#e6e2d6] bg-white px-4 py-2 text-sm font-medium text-[#1a2947] hover:bg-[#1a2947] hover:text-white transition-colors"
          data-testid="family-login-home-btn"
        >
          <Home className="h-4 w-4" /> Home
        </Link>
      </header>

      <main className="max-w-md mx-auto px-6 md:px-10 py-10">
        <div className="label-eyebrow mb-2">Family sign-in</div>
        <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight mb-3">
          Welcome back.
        </h1>
        <p className="text-stone-600 mb-8">
          Sign in with the email and password you set on your family card. If
          you haven't set one, open your invite link instead — that always
          works.
        </p>

        <form
          onSubmit={submit}
          className="bg-white border border-[#e6e2d6] rounded-2xl p-6 space-y-4"
          data-testid="family-login-form"
        >
          <div className="space-y-2">
            <Label htmlFor="fl-email">Email</Label>
            <Input
              id="fl-email"
              type="email"
              inputMode="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="bg-white border-stone-200"
              data-testid="family-login-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fl-pass">Password</Label>
            <Input
              id="fl-pass"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="bg-white border-stone-200"
              data-testid="family-login-password"
            />
          </div>

          <label
            className="flex items-start gap-2 text-sm text-stone-600 cursor-pointer select-none"
            data-testid="family-login-remember-label"
          >
            <Checkbox
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
              className="mt-0.5"
              data-testid="family-login-remember"
            />
            <span>
              Remember this device for {REMEMBER_DAYS} days
              <span className="block text-xs text-stone-400 mt-0.5">
                Skip the password on this browser next time. Uncheck on shared
                or public devices.
              </span>
            </span>
          </label>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] text-white"
            data-testid="family-login-submit"
          >
            <LogIn className="h-4 w-4 mr-2" />
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <p className="text-xs text-stone-500 mt-6 text-center">
          Forgot your password? Open your original invite email and use the
          magic link to reset it from your card.
        </p>
      </main>
    </div>
  );
}
